import type { ChartInstance } from "../kline-helpers";
import {
  DEFAULT_ARROWED_LINE_STYLE,
  DEFAULT_CHANNEL_STYLE,
  DEFAULT_ELLIOTT_STYLE,
  DEFAULT_EQUIDISTANT_CHANNEL_STYLE,
  DEFAULT_FIBONACCI_STYLE,
  DEFAULT_FIB_STYLE,
  DEFAULT_GANN_GRID_STYLE,
  DEFAULT_GANN_LINE_STYLE,
  DEFAULT_LINE_STYLE,
  DEFAULT_PRICE_LABEL_STYLE,
  DEFAULT_SHAPE_STYLE,
  DEFAULT_SIGNAL_ARROW_STYLE,
  DEFAULT_TEXT_LABEL_STYLE,
  DEFAULT_TEXT_STYLE,
  DEFAULT_TREND_BY_ANGLE_STYLE,
  FIBONACCI_DEFAULT_LEVELS,
} from "@/lib/chart-drawings/defaults";
import { computeChannelFromRegression } from "./drawing-math";
import { getDrawingDefinition } from "@/lib/chart-drawings/registry";
import type {
  DrawingInstance,
  DrawingInstanceSettings,
  DrawingPoint,
  DrawingType,
} from "@/lib/chart-drawings/types";
import {
  adjustOverlayPoints,
  buildKLineLineStyles,
  loadExtendDataOverlay,
  pressedMoveEndHandler,
  recreateExtendDataOverlay,
  safeCreateOverlay,
  safeOverrideOverlay,
  synthesizeFutureTimestamps,
  updateExtendDataOverlay,
} from "./overlay-utils";
import type { DrawingEngine, DrawingEngineContext, OnPointsChange } from "./types";

function instanceFromOverlay(
  overlay: {
    id: string;
    paneId: string;
    points: DrawingPoint[];
  },
  ctx: DrawingEngineContext,
  type: DrawingType,
  name: string,
  settings: DrawingInstanceSettings
): Omit<DrawingInstance, "addedAt"> {
  return {
    id: overlay.id,
    name,
    symbolKey: ctx.symbol,
    windowId: overlay.paneId,
    chartPanelId: ctx.chartPanelId,
    type,
    visible: true,
    settings,
  };
}

function createLineEngine(
  drawingId: DrawingType,
  overlayName: string,
  displayName: string,
  requiredPoints: number
): DrawingEngine {
  const defaultStyles = buildKLineLineStyles(DEFAULT_LINE_STYLE);

  return {
    drawingId,
    overlayName,
    requiredPoints,
    create(chart, ctx) {
      safeCreateOverlay(
        chart,
        {
          name: overlayName,
          styles: defaultStyles,
          onDrawEnd: ({ overlay }: { overlay: { id: string; paneId: string; points: DrawingPoint[] } }) => {
            if (overlay?.id && overlay.points?.length >= requiredPoints) {
              ctx.addInstance(
                instanceFromOverlay(overlay, ctx, drawingId, displayName, {
                  points: synthesizeFutureTimestamps(chart, overlay.points),
                  style: { ...DEFAULT_LINE_STYLE },
                })
              );
            }
          },
          onPressedMoveEnd: pressedMoveEndHandler(chart, (id, points) =>
            ctx.updateSettings(id, { points })
          ),
        },
        drawingId
      );
    },
    load(chart, instance, onPointsChange) {
      const points = instance.settings?.points;
      if (!points?.length) return;
      safeCreateOverlay(
        chart,
        {
          name: overlayName,
          id: instance.id,
          paneId: instance.windowId,
          points: adjustOverlayPoints(chart, points),
          lock: false,
          visible: instance.visible !== false,
          styles: buildKLineLineStyles(instance.settings?.style),
          onPressedMoveEnd: pressedMoveEndHandler(chart, onPointsChange),
        },
        drawingId
      );
    },
    update(chart, instance) {
      if (!instance.settings?.style) return;
      safeOverrideOverlay(
        chart,
        {
          id: instance.id,
          visible: instance.visible !== false,
          styles: buildKLineLineStyles(instance.settings.style),
        },
        drawingId
      );
    },
    recreate(chart, instance, onPointsChange) {
      this.update(chart, instance);
      if (instance.settings?.points) {
        safeOverrideOverlay(
          chart,
          {
            id: instance.id,
            points: adjustOverlayPoints(chart, instance.settings.points),
          },
          drawingId
        );
      }
      void onPointsChange;
    },
  };
}

function mergeRegressionComputed(
  chart: ChartInstance,
  settings: DrawingInstanceSettings,
  points: DrawingPoint[]
): DrawingInstanceSettings {
  const deviations =
    typeof settings.values?.Deviations === "number"
      ? (settings.values.Deviations as number)
      : 2;
  const computed = computeChannelFromRegression(chart, points, deviations);
  return { ...settings, points, computed: computed ?? settings.computed };
}

function createExtendDataEngine(
  drawingId: DrawingType,
  overlayName: string,
  displayName: string,
  requiredPoints: number,
  defaultSettings: DrawingInstanceSettings,
  options?: {
    requiresTextPrompt?: boolean;
    recomputeRegression?: boolean;
  }
): DrawingEngine {
  return {
    drawingId,
    overlayName,
    requiredPoints,
    requiresTextPrompt: options?.requiresTextPrompt,
    create(chart, ctx) {
      const extendData = { settings: defaultSettings };
      safeCreateOverlay(
        chart,
        {
          name: overlayName,
          extendData,
          onDrawEnd: ({ overlay }: { overlay: { id: string; paneId: string; points: DrawingPoint[] } }) => {
            if (overlay?.id && overlay.points?.length >= requiredPoints) {
              const points = synthesizeFutureTimestamps(chart, overlay.points);
              let settings: DrawingInstanceSettings = {
                ...defaultSettings,
                points,
              };
              if (options?.recomputeRegression) {
                settings = mergeRegressionComputed(chart, settings, points);
              }
              ctx.addInstance(
                instanceFromOverlay(overlay, ctx, drawingId, displayName, settings)
              );
            }
          },
          onPressedMoveEnd: pressedMoveEndHandler(chart, (id, points) => {
            if (options?.recomputeRegression) {
              ctx.updateSettings(
                id,
                mergeRegressionComputed(chart, { points }, points)
              );
            } else {
              ctx.updateSettings(id, { points });
            }
          }),
        },
        drawingId
      );
    },
    load(chart, instance, onPointsChange) {
      const inst =
        options?.recomputeRegression && instance.settings?.points?.length
          ? {
              ...instance,
              settings: mergeRegressionComputed(
                chart,
                instance.settings,
                instance.settings.points!
              ),
            }
          : instance;
      loadExtendDataOverlay(
        chart,
        inst,
        overlayName,
        requiredPoints,
        onPointsChange
      );
    },
    update(chart, instance) {
      updateExtendDataOverlay(chart, instance);
    },
    recreate(chart, instance, onPointsChange) {
      const inst =
        options?.recomputeRegression && instance.settings?.points?.length
          ? {
              ...instance,
              settings: mergeRegressionComputed(
                chart,
                instance.settings,
                instance.settings.points!
              ),
            }
          : instance;
      recreateExtendDataOverlay(chart, inst, overlayName, onPointsChange);
    },
  };
}

const horizontalLineEngine = createLineEngine(
  "horizontal-line",
  "horizontalStraightLine",
  "Horizontal Line",
  1
);

const verticalLineEngine = createLineEngine(
  "vertical-line",
  "verticalStraightLine",
  "Vertical Line",
  1
);

const trendlineEngine = createLineEngine(
  "trendline",
  "straightLine",
  "Trendline",
  2
);

const rectangleEngine = createExtendDataEngine(
  "rectangle",
  "customRect",
  "Rectangle",
  2,
  { fill: true, style: DEFAULT_SHAPE_STYLE }
);

const ellipseEngine = createExtendDataEngine(
  "ellipse",
  "customEllipse",
  "Ellipse",
  2,
  { fill: true, style: DEFAULT_SHAPE_STYLE }
);

const triangleEngine = createExtendDataEngine(
  "triangle",
  "customTriangle",
  "Triangle",
  3,
  { fill: true, style: DEFAULT_SHAPE_STYLE }
);

const arrowedLineEngine = createExtendDataEngine(
  "arrowed-line",
  "customArrowedLine",
  "Arrow",
  2,
  { style: DEFAULT_ARROWED_LINE_STYLE }
);

const equidistantChannelEngine = createExtendDataEngine(
  "equidistant-channel",
  "customEquidistantChannel",
  "Equidistant Channel",
  3,
  { fill: true, style: DEFAULT_EQUIDISTANT_CHANNEL_STYLE }
);

const elliottMotiveWaveEngine = createExtendDataEngine(
  "elliott-motive-wave",
  "customElliottMotiveWave",
  "Elliott Motive Wave",
  5,
  { style: DEFAULT_ELLIOTT_STYLE }
);

const gannGridEngine = createExtendDataEngine(
  "gann-grid",
  "customGannGrid",
  "Gann Grid",
  2,
  { style: DEFAULT_GANN_GRID_STYLE }
);

/**
 * Fibonacci Retracement.
 *
 * Moved off KLineCharts' built-in `fibonacciLine` overlay — that one spans the
 * whole pane by design and cannot express bands, price tags or per-level
 * emphasis. `customFibonacciRetracement` shares the bounded/banded/tagged
 * renderer with the other four Fibonacci tools.
 */
const fibonacciRetracementEngine = createExtendDataEngine(
  "fibonacci-retracement",
  "customFibonacciRetracement",
  "Fibonacci Retracement",
  2,
  { style: DEFAULT_FIBONACCI_STYLE, levels: FIBONACCI_DEFAULT_LEVELS }
);

function priceLabelText(instance: DrawingInstance): string {
  const t = instance.settings?.text;
  if (typeof t === "string" && t.length > 0) return t;
  const v = instance.settings?.points?.[0]?.value;
  return typeof v === "number" ? v.toFixed(5) : "0.00000";
}

const priceLabelEngine: DrawingEngine = {
  drawingId: "price-label",
  overlayName: "customPriceLabel",
  requiredPoints: 1,
  create(chart, ctx) {
    const settings: DrawingInstanceSettings = {
      style: DEFAULT_PRICE_LABEL_STYLE,
    };
    safeCreateOverlay(
      chart,
      {
        name: "customPriceLabel",
        extendData: { settings },
        onDrawEnd: ({ overlay }: { overlay: { id: string; paneId: string; points: DrawingPoint[] } }) => {
          if (overlay?.id && overlay.points?.length > 0) {
            const priceText =
              overlay.points[0]?.value?.toFixed(5) ?? "0.00000";
            ctx.addInstance(
              instanceFromOverlay(overlay, ctx, "price-label", "Price Label", {
                points: synthesizeFutureTimestamps(chart, overlay.points),
                text: priceText,
                style: DEFAULT_PRICE_LABEL_STYLE,
              })
            );
          }
        },
        onPressedMoveEnd: pressedMoveEndHandler(chart, (id, points) =>
          ctx.updateSettings(id, { points })
        ),
      },
      "price-label"
    );
  },
  load(chart, instance, onPointsChange) {
    const points = instance.settings?.points;
    if (!points?.length) return;
    safeCreateOverlay(
      chart,
      {
        name: "customPriceLabel",
        id: instance.id,
        paneId: instance.windowId,
        points: adjustOverlayPoints(chart, points),
        lock: false,
        visible: instance.visible !== false,
        extendData: {
          settings: {
            ...instance.settings,
            text: priceLabelText(instance),
          },
        },
        onPressedMoveEnd: pressedMoveEndHandler(chart, onPointsChange),
      },
      "price-label"
    );
  },
  update(chart, instance) {
    safeOverrideOverlay(
      chart,
      {
        id: instance.id,
        visible: instance.visible !== false,
        extendData: {
          settings: {
            ...instance.settings,
            text: priceLabelText(instance),
          },
        },
      },
      "price-label"
    );
  },
  recreate(chart, instance, onPointsChange) {
    try {
      chart.removeOverlay(instance.id);
    } catch {
      /* ignore */
    }
    this.load(chart, instance, onPointsChange);
  },
};

const trendByAngleEngine = createExtendDataEngine(
  "trend-by-angle",
  "customTrendByAngle",
  "Trendline by angle",
  2,
  { style: DEFAULT_TREND_BY_ANGLE_STYLE, angle: 45, values: { Angle: 45 } }
);

const stdDevChannelEngine = createExtendDataEngine(
  "stddev-channel",
  "customStdDevChannel",
  "Standard deviation channel",
  2,
  { style: DEFAULT_CHANNEL_STYLE, values: { Deviations: 2 } },
  { recomputeRegression: true }
);

const regressionChannelEngine = createExtendDataEngine(
  "regression-channel",
  "customRegressionChannel",
  "Regression channel",
  2,
  { style: DEFAULT_CHANNEL_STYLE, values: { Deviations: 2 } },
  { recomputeRegression: true }
);

const andrewsPitchforkEngine = createExtendDataEngine(
  "andrews-pitchfork",
  "customAndrewsPitchfork",
  "Andrews' pitchfork",
  3,
  { style: DEFAULT_CHANNEL_STYLE }
);

const fibonacciTimeZonesEngine = createExtendDataEngine(
  "fibonacci-time-zones",
  "customFibonacciTimeZones",
  "Fibonacci time zones",
  2,
  { style: DEFAULT_FIB_STYLE, levels: FIBONACCI_DEFAULT_LEVELS }
);

const fibonacciFanEngine = createExtendDataEngine(
  "fibonacci-fan",
  "customFibonacciFan",
  "Fibonacci fan",
  2,
  { style: DEFAULT_FIB_STYLE, levels: FIBONACCI_DEFAULT_LEVELS }
);

const fibonacciArcsEngine = createExtendDataEngine(
  "fibonacci-arcs",
  "customFibonacciArcs",
  "Fibonacci arcs",
  2,
  { style: DEFAULT_FIB_STYLE, levels: FIBONACCI_DEFAULT_LEVELS }
);

const fibonacciChannelEngine = createExtendDataEngine(
  "fibonacci-channel",
  "customFibonacciChannel",
  "Fibonacci channel",
  3,
  { style: DEFAULT_FIB_STYLE, levels: FIBONACCI_DEFAULT_LEVELS }
);

const fibonacciExpansionEngine = createExtendDataEngine(
  "fibonacci-expansion",
  "customFibonacciExpansion",
  "Fibonacci expansion",
  3,
  { style: DEFAULT_FIB_STYLE, levels: FIBONACCI_DEFAULT_LEVELS }
);

const gannLineEngine = createExtendDataEngine(
  "gann-line",
  "customGannLine",
  "Gann line",
  2,
  { style: DEFAULT_GANN_LINE_STYLE }
);

const gannFanEngine = createExtendDataEngine(
  "gann-fan",
  "customGannFan",
  "Gann fan",
  2,
  { style: DEFAULT_GANN_GRID_STYLE, values: { Divisions: 8 } }
);

const signalArrowUpEngine = createExtendDataEngine(
  "signal-arrow-up",
  "customSignalArrowUp",
  "Arrow up",
  1,
  { style: DEFAULT_SIGNAL_ARROW_STYLE }
);

const signalArrowDownEngine = createExtendDataEngine(
  "signal-arrow-down",
  "customSignalArrowDown",
  "Arrow down",
  1,
  { style: { ...DEFAULT_SIGNAL_ARROW_STYLE, color: "#ef5350" } }
);

const cycleLinesEngine = createExtendDataEngine(
  "cycle-lines",
  "customCycleLines",
  "Cycle lines",
  2,
  { style: DEFAULT_LINE_STYLE }
);

const textLabelEngine: DrawingEngine = {
  drawingId: "text-label",
  overlayName: "customTextLabel",
  requiredPoints: 1,
  requiresTextPrompt: true,
  create(chart, ctx) {
    if (!ctx.textValue) {
      console.warn("Text value is required for text-label drawing");
      return;
    }
    const settings: DrawingInstanceSettings = {
      text: ctx.textValue,
      style: DEFAULT_TEXT_LABEL_STYLE,
    };
    safeCreateOverlay(
      chart,
      {
        name: "customTextLabel",
        extendData: { settings },
        onDrawEnd: ({ overlay }: { overlay: { id: string; paneId: string; points: DrawingPoint[] } }) => {
          if (overlay?.id && overlay.points?.length >= 1) {
            ctx.addInstance(
              instanceFromOverlay(overlay, ctx, "text-label", "Text label", {
                ...settings,
                points: synthesizeFutureTimestamps(chart, overlay.points),
              })
            );
          }
        },
        onPressedMoveEnd: pressedMoveEndHandler(chart, (id, points) =>
          ctx.updateSettings(id, { points })
        ),
      },
      "text-label"
    );
  },
  load(chart, instance, onPointsChange) {
    if (!instance.settings?.points?.length || !instance.settings.text) return;
    loadExtendDataOverlay(chart, instance, "customTextLabel", 1, onPointsChange);
  },
  update(chart, instance) {
    updateExtendDataOverlay(chart, instance);
  },
  recreate(chart, instance, onPointsChange) {
    recreateExtendDataOverlay(chart, instance, "customTextLabel", onPointsChange);
  },
};

const elliottCorrectiveWaveEngine = createExtendDataEngine(
  "elliott-corrective-wave",
  "customElliottCorrectiveWave",
  "Elliott correction wave (ABC)",
  3,
  { style: DEFAULT_ELLIOTT_STYLE }
);

const textEngine: DrawingEngine = {
  drawingId: "text",
  overlayName: "customText",
  requiredPoints: 1,
  requiresTextPrompt: true,
  create(chart, ctx) {
    if (!ctx.textValue) {
      console.warn("Text value is required for text drawing");
      return;
    }
    const settings: DrawingInstanceSettings = {
      text: ctx.textValue,
      style: DEFAULT_TEXT_STYLE,
    };
    safeCreateOverlay(
      chart,
      {
        name: "customText",
        styles: { text: { color: DEFAULT_TEXT_STYLE.color, size: DEFAULT_TEXT_STYLE.fontSize } },
        extendData: { settings },
        onDrawEnd: ({ overlay }: { overlay: { id: string; paneId: string; points: DrawingPoint[] } }) => {
          if (overlay?.id && overlay.points?.length >= 1) {
            ctx.addInstance(
              instanceFromOverlay(overlay, ctx, "text", "Text", {
                ...settings,
                points: synthesizeFutureTimestamps(chart, overlay.points),
              })
            );
          }
        },
        onPressedMoveEnd: pressedMoveEndHandler(chart, (id, points) =>
          ctx.updateSettings(id, { points })
        ),
      },
      "text"
    );
  },
  load(chart, instance, onPointsChange) {
    if (!instance.settings?.points?.length || !instance.settings.text) return;
    loadExtendDataOverlay(chart, instance, "customText", 1, onPointsChange);
  },
  update(chart, instance) {
    updateExtendDataOverlay(chart, instance);
  },
  recreate(chart, instance, onPointsChange) {
    recreateExtendDataOverlay(chart, instance, "customText", onPointsChange);
  },
};

export const DRAWING_ENGINES: Record<string, DrawingEngine> = {
  "vertical-line": verticalLineEngine,
  "horizontal-line": horizontalLineEngine,
  trendline: trendlineEngine,
  "trend-by-angle": trendByAngleEngine,
  "equidistant-channel": equidistantChannelEngine,
  "stddev-channel": stdDevChannelEngine,
  "regression-channel": regressionChannelEngine,
  "andrews-pitchfork": andrewsPitchforkEngine,
  "fibonacci-retracement": fibonacciRetracementEngine,
  "fibonacci-time-zones": fibonacciTimeZonesEngine,
  "fibonacci-fan": fibonacciFanEngine,
  "fibonacci-arcs": fibonacciArcsEngine,
  "fibonacci-channel": fibonacciChannelEngine,
  "fibonacci-expansion": fibonacciExpansionEngine,
  "gann-line": gannLineEngine,
  "gann-fan": gannFanEngine,
  "gann-grid": gannGridEngine,
  rectangle: rectangleEngine,
  ellipse: ellipseEngine,
  triangle: triangleEngine,
  "arrowed-line": arrowedLineEngine,
  "signal-arrow-up": signalArrowUpEngine,
  "signal-arrow-down": signalArrowDownEngine,
  "cycle-lines": cycleLinesEngine,
  "price-label": priceLabelEngine,
  text: textEngine,
  "text-label": textLabelEngine,
  "elliott-motive-wave": elliottMotiveWaveEngine,
  "elliott-corrective-wave": elliottCorrectiveWaveEngine,
};

export function getDrawingEngine(drawingId: DrawingType): DrawingEngine | undefined {
  return DRAWING_ENGINES[drawingId];
}

export function createDrawing(
  chart: ChartInstance,
  drawingId: DrawingType,
  ctx: DrawingEngineContext
): void {
  const engine = getDrawingEngine(drawingId);
  if (!engine) {
    console.warn(`No drawing engine for type: ${drawingId}`);
    return;
  }
  const def = getDrawingDefinition(drawingId);
  if (def && !def.enabled) {
    console.warn(`Drawing type not enabled: ${drawingId}`);
    return;
  }
  engine.create(chart, ctx);
}
